import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonDataTypes = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Core Concepts</div>
                <h1>Python Data Types Architecture</h1>
                <p className="hero-lead">
                    Master the foundational building blocks of Python. Explore the CPython memory model,
                    understand the mechanics of dynamic typing, and learn why "everything is an object"
                    functions as more than just a catchy slogan.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    In low-level languages like C, a "variable" is fundamentally a direct memory address where distinct values are stored.
                    If you declare <code>int x = 5</code>, the compiler reserves 4 bytes of memory and writes the binary representation
                    of 5 into it.
                </p>
                <p>
                    Python is fundamentally different. In Python, variables are not boxes that hold data; they are <strong>labels</strong> (or references)
                    attached to objects living in memory. When you write <code>x = 5</code>, Python creates an object representing the integer 5
                    and attaches the label <code>x</code> to it. If you later write <code>x = "hello"</code>, you strip the label from the integer
                    and attach it to a new string object.
                </p>
                <p>
                    This "Dynamic Typing" system is powerful but requires a solid mental model of how memory works.
                    In this deep dive, we will dissect the type system, explore the critical distinction between mutable and immutable types,
                    and uncover performance optimizations like "String Interning" and "Small Integer Caching" that happen under the hood.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>The Memory Model:</strong> How Variables behave as references, not containers.</li>
                        <li><strong>Mutability:</strong> Why some objects change in place while others create copies.</li>
                        <li><strong>Numeric Precision:</strong> Why <code>0.1 + 0.2 != 0.3</code> and how to fix it.</li>
                        <li><strong>Internal Optimizations:</strong> How Python caches small integers to save memory.</li>
                        <li><strong>Type Checking:</strong> Why <code>isinstance()</code> is better than <code>type()</code>.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Type Hierarchy: Everything is an Object</h2>
                <p>
                    In Python, functions are objects. Classes are objects. Modules are objects. Even the standard types like <code>int</code>
                    and <code>str</code> are instances of the metaclass <code>type</code>. All of these ultimately inherit from the base <code>object</code> class.
                </p>

                <div className="comparison-table">
                    <h3>Python's Core Data Types</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Type Name</th>
                                <th>Examples</th>
                                <th>Mutable?</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Numeric</strong></td>
                                <td><code>int</code>, <code>float</code>, <code>complex</code></td>
                                <td><code>42</code>, <code>3.14</code>, <code>1+2j</code></td>
                                <td> No</td>
                            </tr>
                            <tr>
                                <td><strong>Text Sequence</strong></td>
                                <td><code>str</code></td>
                                <td><code>"Python"</code>, <code>'v3.12'</code></td>
                                <td> No</td>
                            </tr>
                            <tr>
                                <td><strong>Sequence</strong></td>
                                <td><code>list</code>, <code>tuple</code>, <code>range</code></td>
                                <td><code>[1, 2]</code>, <code>(1, 2)</code></td>
                                <td>List only</td>
                            </tr>
                            <tr>
                                <td><strong>Mapping</strong></td>
                                <td><code>dict</code></td>
                                <td><code>{'{'}"key": "val"{'}'}</code></td>
                                <td>Yes</td>
                            </tr>
                            <tr>
                                <td><strong>Set Types</strong></td>
                                <td><code>set</code>, <code>frozenset</code></td>
                                <td><code>{'{'}1, 2{'}'}</code></td>
                                <td>Set only</td>
                            </tr>
                            <tr>
                                <td><strong>Binary</strong></td>
                                <td><code>bytes</code>, <code>bytearray</code></td>
                                <td><code>b"data"</code></td>
                                <td>Bytearray</td>
                            </tr>
                            <tr>
                                <td><strong>Null</strong></td>
                                <td><code>NoneType</code></td>
                                <td><code>None</code></td>
                                <td> No</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Introspection with type() and isinstance()</span>
                    </div>
                    <CodeBlock language="python" code={`# Checking types
x = 100
print(type(x))  # <class 'int'>

#  Don't compare types directly!
# Bad: if type(x) == int:
# Good: isinstance() handles inheritance
class SuperInt(int): pass

n = SuperInt(5)
print(type(n) == int)      # False (Strict check fails)
print(isinstance(n, int))  # True (It behaves like an int)`} />
                </div>

                <h3>Code Walkthrough</h3>
                <p>
                    The <code>isinstance(obj, class)</code> function is the "polymorphic" way to check types. It returns <code>True</code>
                    not just if the object is that exact class, but also if it <em>inherits</em> from that class. This follows the
                    Liskov Substitution Principle: if a function expects an <code>int</code>, it should also accept a subclass of <code>int</code>.
                </p>
            </div>

            <div className="content-section">
                <h2>History Lesson: The Great String Schism (Python 2 vs 3)</h2>
                <p>
                    If you read older code, you might see <code>u"hello"</code> or <code>unicode()</code>.
                    In Python 2, the default string type <code>str</code> was just a sequence of raw bytes (like ASCII).
                    To support accents, emojis, and global languages, you had to explicitly use "unicode strings".
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Python 2 vs Python 3 (Mental Model)</span>
                    </div>
                    <CodeBlock language="python" code={`# Python 2 (Legacy)
x = "Hello"   # This was BYTES (ASCII)
y = u"Hllo"  # This was Unicode

# Python 3 (Modern)
x = "Hllo"   # This is UNICODE by default!
y = b"Data"   # This is BYTES (binary data)

# Why the change?
# Mixing bytes and unicode in Python 2 caused the infamous
# UnicodeDecodeError: 'ascii' codec can't decode byte...
# Python 3 forces you to be explicit: You must .encode() to get bytes
# and .decode() to get text.`} />
                </div>
                <p>
                    This change was painful at the time but made Python the world-class language it is today for handling text processing and web development.
                </p>
            </div>

            <div className="content-section">
                <h2>Memory Deep Dive: Mutability & References</h2>
                <p>
                    This is the single most important concept in Python data structures.
                    <strong>Immutable</strong> objects cannot be changed after creation. <strong>Mutable</strong> objects can be modified in place.
                </p>
                <p>
                    Since variables are just references (pointers) to objects, modifying a mutable object (like a list) will affect <em>every variable</em>
                    that points to it.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The "Shared Reference" Trap</span>
                    </div>
                    <CodeBlock language="python" code={`# SCENARIO 1: Immutable (Integer)
a = 10
b = a      # b points to the SAME object (10) as a
a = 20     # This creates a NEW object (20) and moves the label 'a'
           # 'b' still points to the old object (10)

print(f"a: {a}, b: {b}")  # a: 20, b: 10 (Safe!)


# SCENARIO 2: Mutable (List)
list_a = [1, 2, 3]
list_b = list_a   # list_b points to the SAME list object
list_a.append(4)  # Modifies the object IN PLACE

print(f"a: {list_a}")  # [1, 2, 3, 4]
print(f"b: {list_b}")  # [1, 2, 3, 4] (Changed!)

# SCENARIO 3: Preventing the trap with .copy()
list_c = [1, 2, 3]
list_d = list_c.copy()  # Creates a NEW distinct list object
list_c.append(4)

print(f"c: {list_c}")   # [1, 2, 3, 4]
print(f"d: {list_d}")   # [1, 2, 3] (Safe!)`} />
                </div>

                <h3>Under the Hood: The `id()` Function</h3>
                <p>
                    You can prove this behavior using the built-in <code>id()</code> function, which returns the memory address of an object (in CPython).
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Visualizing Memory Addresses</span>
                    </div>
                    <CodeBlock language="python" code={`x = [1, 2]
y = x
print(id(x) == id(y))  # True (Same address)

y = x.copy()
print(id(x) == id(y))  # False (Different addresses)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Numeric Precision: Floats vs Integers</h2>
                <p>
                    Python Integers are magical. In languages like C or Java, an integer is limited to 32 or 64 bits. If you exceed
                    <code>2,147,483,647</code>, you get an overflow error.
                    <strong>Python integers have arbitrary precision.</strong> They can be as large as your RAM allows.
                </p>

                <h3>The Floating Point Tragedy</h3>
                <p>
                    Floats, however, are standard IEEE 754 double-precision numbers. They cannot represent all decimal fractions exactly.
                    This leads to the infamous "0.30000000000000004" problem.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Float Imprecision & The Decimal Fix</span>
                    </div>
                    <CodeBlock language="python" code={`# The Problem
val = 0.1 + 0.2
print(val)          # 0.30000000000000004
print(val == 0.3)   # False! 

# The Fix: Use the Decimal module for financial math
from decimal import Decimal

d_val = Decimal('0.1') + Decimal('0.2')
print(d_val)         # 0.3
print(d_val == Decimal('0.3'))  # True`} />
                </div>

                <h3>Code Walkthrough</h3>
                <ul>
                    <li><strong>Line 2:</strong> Computers store numbers in binary (base-2). 0.1 is 1/10, which has a repeating binary expansion (like 1/3 in decimal), so it gets truncated.</li>
                    <li><strong>Line 8:</strong> <code>Decimal</code> stores numbers as digits (base-10), just like humans write them, avoiding conversion errors. Always pass strings <code>'0.1'</code> to Decimal, not floats <code>0.1</code>!</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>CPython Internals: Optimization Secrets</h2>
                <p>
                    To make Python faster, the CPython interpreter (the standard Python) uses several tricks to avoid allocating memory unnecessarily.
                    Understanding these can explain "weird" behavior during debugging.
                </p>

                <h3>1. Small Integer Caching</h3>
                <p>
                    Python pre-allocates integers from <strong>-5 to 256</strong> when the interpreter starts.
                    Every time you access these numbers, you get a reference to the existing singleton object.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Integer Interning Demo</span>
                    </div>
                    <CodeBlock language="python" code={`# Small integers are cached
a = 100
b = 100
print(a is b)  # True (Same memory address)

# Large integers are NOT cached (usually)
x = 1000
y = 1000
print(x is y)  # False (Different objects)

# Note: Some IDEs/Compilers might optimize 'x = 1000; y = 1000' 
# within the same code block, masking this behavior.`} />
                </div>

                <h3>2. String Interning</h3>
                <p>
                    Python automatically "interns" (caches) strings that look like identifiers (letters, numbers, underscores).
                    This allows for faster dictionary lookups since string comparison becomes a pointer comparison check.
                </p>
            </div>

            <div className="content-section">
                <h2>The Special Case of 'None'</h2>
                <p>
                    <code>None</code> is Python's Null. It represents the absence of a value.
                    Crucially, <code>None</code> is a <strong>Singleton</strong>. There is only ever one <code>None</code> object in the entire system.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Checking for None</span>
                    </div>
                    <CodeBlock language="python" code={`val = None

# The Correct Way (Identity Check)
if val is None:
    print("It's empty")

#  The Wrong Way (Equality Check)
if val == None:
    print("It's empty")
    
# Why? A custom class could implement __eq__ to return True
# even if it's not actually None.`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Performance Note:</strong> The <code>is</code> operator is faster than <code>==</code>.
                        <code>is</code> simply compares two integer memory addresses. <code>==</code> has to call the <code>__eq__</code> method,
                        handle type checking, and run comparison logic.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Advanced Memory Management: Garbage Collection</h2>
                <p>
                    We established that variables are references to objects. But what happens when an object has no references?
                    Example: <code>x = 10; x = 20</code>. The integer object <code>10</code> is now "orphaned".
                    Python's Memory Manager handles this automatically via two mechanisms.
                </p>

                <h3>1. Reference Counting (Primary)</h3>
                <p>
                    Every object in Python contains a field <code>ob_refcnt</code>. When you assign <code>x = obj</code>, count goes up.
                    When you delete <code>del x</code> or reassign, count goes down. When references hit 0, the memory is instantly reclaimed.
                    This is deterministic and fast.
                </p>

                <h3>2. Cyclic Garbage Collector (Secondary)</h3>
                <p>
                    What if Object A refers to Object B, and Object B refers to Object A? Their reference counts never hit 0, even if the rest of
                    the program can't access them! This is a "Reference Cycle".
                    Python has a separate Garbage Collector (GC) that periodically wakes up, pauses your program, scans for these cycles,
                    and cleans them up.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Creating a Memory Leaks (Cycle)</span>
                    </div>
                    <CodeBlock language="python" code={`import gc

# Define a class
class Node:
    def __init__(self, value):
        self.value = value
        self.next = None

# Create a cycle
node1 = Node(1)
node2 = Node(2)
node1.next = node2
node2.next = node1  # Cycle!

# Delete references
del node1
del node2
# At this point, ref counts are 1 (pointing to each other).
# They are technically garbage but Ref Counting can't see it.

# Manually trigger GC (usually automatic)
gc.collect()`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Deep Dive: How Python Integers Work</h2>
                <p>
                    We mentioned Python Integers have "Arbitrary Precision". How?
                    In CPython, an <code>int</code> is a C struct containing an array of digits (stored in base-2^30).
                </p>
                <p>
                    When you calculate <code>2 ** 1000</code>, Python dynamically expands this array to store the result.
                    This makes Python slower at math than C (which uses CPU-native 64-bit integers) but infinitely more flexible.
                    However, this abstraction has a cost: a simple integer takes <strong>28 bytes</strong> of memory overhead in Python!
                </p>
            </div>

            <div className="content-section">
                <h2>Performance Optimization: __slots__</h2>
                <p>
                    By default, Python objects store their instance variables in a dictionary (<code>__dict__</code>).
                    This allows you to add new attributes at runtime, but dictionaries use a lot of RAM.
                    If you are creating <em>millions</em> of objects (e.g., points in a 3D game or pixels), this overhead kills performance.
                </p>
                <p>
                    The fix? <code>__slots__</code>.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Saving 50% RAM with __slots__</span>
                    </div>
                    <CodeBlock language="python" code={`class Point:
    # Tell Python: "Don't use a dictionary! Just reserve space for x and y."
    __slots__ = ['x', 'y']
    
    def __init__(self, x, y):
        self.x = x
        self.y = y

p = Point(1, 2)
p.x = 10
# p.z = 5  # AttributeError! Can't add new attributes dynamically.`} />
                </div>

                <h3>Code Walkthrough</h3>
                <p>
                    Using <code>__slots__</code> removes the dynamic <code>__dict__</code> and prevents adding new attributes, but it
                    makes attribute access faster and reduces memory usage significantly (often by 40-50%).
                </p>
            </div>

            <div className="content-section">
                <h2>Bitwise Magic: Integers as Bits</h2>
                <p>
                    Since computers execute binary logic, Python exposes direct access to these operations via Bitwise Operators.
                    These are crucial for networking (flags), cryptography, and lower-level optimization.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Binary Manipulation</span>
                    </div>
                    <CodeBlock language="python" code={`x = 10       # Binary: 1010
y = 4        # Binary: 0100

print(x & y)   # Bitwise AND: 0000 -&gt; 0
print(x | y)   # Bitwise OR:  1110 -&gt; 14
print(x ^ y)   # Bitwise XOR: 1110 -&gt; 14
print(x << 1)  # Left Shift: 10100 -&gt; 20 (Multiply by 2)
print(x >> 1)  # Right Shift: 0101 -&gt; 5  (Divide by 2)

# Binary Representation Helper
print(bin(x))  # '0b1010'`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Under the Hood: Python Bytecode</h2>
                <p>
                    When you run a script, Python compiles it into Bytecode - a low-level set of instructions for the Python Virtual Machine (PVM).
                    We can inspect this using the <code>dis</code> module to see <em>exactly</em> what operations are happening.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Disassembling Code</span>
                    </div>
                    <CodeBlock language="python" code={`import dis

def add(a, b):
    return a + b

# Show the Bytecode instructions
dis.dis(add)`} />
                </div>

                <h3>Output Explanation</h3>
                <CodeBlock language="text" code={`  2           0 LOAD_FAST                0 (a)
              2 LOAD_FAST                1 (b)
              4 BINARY_ADD
              6 RETURN_VALUE`} />
                <p>
                    This confirms that adding two variables involves pushing them onto the stack (<code>LOAD_FAST</code>),
                    executing the addition (<code>BINARY_ADD</code>), and returning the result. No magic, just operations.
                </p>
            </div>

            <div className="content-section">
                <h2>Modern Data Models: Enums and Dataclasses</h2>
                <p>
                    Python 3.4+ introduced powerful ways to structure data that go beyond simple dictionaries and classes.
                </p>

                <h3>1. Enumerations (Enum)</h3>
                <p>
                    Stop using "Magic Strings" or integers (0=Red, 1=Blue) to represent fixed states. Enums make code readable and type-safe.
                </p>
                <CodeBlock language="python" code={`from enum import Enum, auto

class Status(Enum):
    PENDING = auto()
    RUNNING = auto()
    COMPLETED = auto()
    FAILED = auto()

def check_job(status):
    # Safer than: if status == "RUNNING":
    if status is Status.RUNNING:
        print("Job is active")

print(Status.PENDING.name)  # 'PENDING'
print(Status.PENDING.value) # 1`} />

                <h3>2. Dataclasses</h3>
                <p>
                    Writing <code>__init__</code>, <code>__repr__</code>, and <code>__eq__</code> for every data-holding class is tedious.
                    Dataclasses (Python 3.7+) automate this. They are essentially "Mutable Structs".
                </p>
                <CodeBlock language="python" code={`from dataclasses import dataclass

@dataclass
class User:
    id: int
    name: str
    email: str
    active: bool = True

# Auto-generated __init__!
u = User(1, "Alice", "alice@example.com")

# Auto-generated __repr__!
print(u) 
# User(id=1, name='Alice', email='alice@example.com', active=True)

# Auto-generated __eq__!
u2 = User(1, "Alice", "alice@example.com")
print(u == u2)  # True (Value equality)`} />
            </div>

            <div className="content-section">
                <h2>Beyond Basics: The Collections Module</h2>
                <p>
                    While lists and dicts are powerful, Python includes specialized container datatypes in the <code>collections</code> module.
                    Knowing these separates intermediate developers from experts.
                </p>

                <h3>1. defaultdict</h3>
                <p>
                    A dictionary that calls a factory function to supply missing values. No more <code>KeyError</code>!
                </p>
                <CodeBlock language="python" code={`from collections import defaultdict

# Counting words without checking keys
word_counts = defaultdict(int)  # Default value is 0
words = ["apple", "banana", "apple", "cherry"]

for word in words:
    word_counts[word] += 1  # No KeyError on first access!

print(dict(word_counts))  # {'apple': 2, 'banana': 1, 'cherry': 1}`} />

                <h3>2. Counter</h3>
                <p>
                    A dict subclass for counting hashable objects. It comes with powerful utility methods.
                </p>
                <CodeBlock language="python" code={`from collections import Counter

# Instant frequency analysis
counts = Counter("mississippi")
print(counts.most_common(2))  # [('i', 4), ('s', 4)]

# Math with counters
c1 = Counter(a=3, b=1)
c2 = Counter(a=1, b=2)
print(c1 + c2)  # Counter({'a': 4, 'b': 3})`} />

                <h3>3. deque (Double-Ended Queue)</h3>
                <p>
                    Lists are optimized for fixed-length operations. Inserting at the beginning <code>list.insert(0, x)</code> is slow (O(n)).
                    <code>deque</code> is optimized for O(1) appends and pops from <em>both</em> ends.
                </p>
            </div>

            <div className="content-section real-world-section">
                <h2>Real-World Application: The Mutability Bug</h2>
                <p>
                    The most common interview question (and production bug) involving data types is the "Mutable Default Argument" trap.
                </p>

                <div className="use-case-grid">
                    <div className="use-case-card">
                        <h3> The Bug</h3>
                        <CodeBlock language="python" code={`def add_employee(emp, list=[]):
    list.append(emp)
    return list

# The default list is created ONCE at definition time!
print(add_employee("Alice"))  # ['Alice']
print(add_employee("Bob"))    # ['Alice', 'Bob'] 
# Bob was added to Alice's list!`} />
                    </div>

                    <div className="use-case-card">
                        <h3>The Fix</h3>
                        <CodeBlock language="python" code={`def add_employee(emp, list=None):
    if list is None:
        list = []  # Create NEW list each call
    list.append(emp)
    return list

print(add_employee("Alice"))  # ['Alice']
print(add_employee("Bob"))    # ['Bob'] (Correct)`} />
                    </div>
                </div>
            </div>

            <div className="content-section best-practices">
                <h2>Best Practices & Takeaways</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use <code>isinstance()</code> instead of <code>type()</code>.</li>
                            <li>Use <code>decimal.Decimal</code> for currency/financials.</li>
                            <li>Use <code>copy()</code> or <code>deepcopy()</code> when working with shared mutable lists.</li>
                            <li>Use <code>None</code> as the default value for mutable function arguments.</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't</h3>
                        <ul>
                            <li>Don't compare <code>x == True</code>. Use <code>if x:</code>.</li>
                            <li>Don't compare <code>x == None</code>. Use <code>if x is None:</code>.</li>
                            <li>Don't assume floats are exact.</li>
                            <li>Don't rely on <code>id()</code> for anything other than debugging.</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonDataTypes;


